import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Link,
  Modal,
  ModalBody, ModalFooter,
  ModalHeader,
  Typography
} from '@one-thd/sui-atomic-components';
import { DO_NOT_CALL, SECURITY_PATH } from '../constants';
import { unsubscribeTelemarketing } from '../apis/telemarketing';
import { formatPhone } from './common/helper';
import { BannerContext, BannerProvider } from '../contexts/BannerContext';
import { addAnalyticEvent } from '../analytics/common';
import {
  noPhoneOverlayEvent, phoneOverlayError,
  phoneOverlayEvent, unsubscribeFromTelemarketing,
} from '../analytics/telemarketing';
import ErrorBoundary from '../contexts/ErrorBoundary';

export const unsubTelemarketing = 'telemarketing-unsub';
export const confirmUnsubscribe = 'telemarketing-confirm-unsubscribe';

export function Preferences({ phoneNumber }) {

  const { closeSuccessAlert, openSuccessAlert, openErrorAlert } = React.useContext(BannerContext);

  const [showPhoneModal, setPhoneModal] = React.useState(false);
  const [showNoPhoneModal, setNoPhoneModal] = React.useState(false);

  const handleUnsubPhoneCallClick = () => {
    addAnalyticEvent(unsubscribeFromTelemarketing);
    closeSuccessAlert();
    if (phoneNumber) {
      setPhoneModal(true);
      addAnalyticEvent(phoneOverlayEvent);
    } else {
      setNoPhoneModal(true);
      addAnalyticEvent(noPhoneOverlayEvent);
    }
  };

  function PhoneModal() {
    function handleSuccess() {
      setPhoneModal(false);
      openSuccessAlert();
    }

    function handleError() {
      addAnalyticEvent(phoneOverlayError);
      setPhoneModal(false);
      openErrorAlert();
    }

    const handleUnsubscribeClick = () => {
      unsubscribeTelemarketing(phoneNumber, handleSuccess, handleError);
    };

    const handleClose = () => {
      setPhoneModal(false);
    };

    return showPhoneModal && (
      <Modal onClose={handleClose} open={showPhoneModal}>
        <ModalHeader onClose={handleClose}>Remove From Call List</ModalHeader>
        <ModalBody>
          <div className="sui-grid sui-gap-4">
            <Typography variant="body-base">
              {`The phone number on your account will be removed from The Home Depot call list.
            This call list is related to promotional and marketing information.
            The Home Depot may still call you on non-promotional and non-marketing matters.
            To edit your phone number, please visit the `}
              <Link href={SECURITY_PATH}>Security</Link>
              {' page before continuing to opt-out of the Call List.'}
            </Typography>
            <div className="sui-text-center">
              <Typography variant="body-base" weight="bold">
                {formatPhone(phoneNumber)}
              </Typography>
            </div>
          </div>
        </ModalBody>
        <ModalFooter position="end">
          <Button aria-label={confirmUnsubscribe} onClick={handleUnsubscribeClick}>
            Confirm
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  function NoPhoneModal() {
    const handleClose = () => {
      setNoPhoneModal(false);
    };

    return (
      <Modal onClose={handleClose} open={showNoPhoneModal}>
        <ModalHeader onClose={handleClose}>Remove From Call List</ModalHeader>
        <ModalBody>
          <Typography variant="body-base">
            {`To be removed from The Home Depot call list, there must be a saved phone number on your account.
          Want to add one now? Go to the `}
            <Link href={SECURITY_PATH}>Security</Link>
            {' page.'}
          </Typography>
        </ModalBody>
        <ModalFooter position="end">
          <Button onClick={handleClose}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  return (
    <div className="sui-grid sui-gap-4">
      <Typography variant="h2" weight="display">PHONE CALLS</Typography>
      <Typography variant="body-base">
        {`On occasion, you may hear from The Home Depot about products, services, special offers, and other
        promotions by phone call. If you do not want to receive promotional phone calls from The Home Depot,
         please click on the ‘Unsubscribe’ button below to be removed from the call list. The Home Depot may
         still call you on non-promotional and non-marketing matters. If wishing to learn more, view
          the `}
        <Link href={DO_NOT_CALL}>Do Not Call Policy</Link>.
      </Typography>
      <div className="sui-grid sui-grid-cols-8">
        <div className="hidden sm:sui-col-span-4 md:sui-col-span-5" />
        <div className="sui-col-span-8 sm:sui-col-span-4 md:sui-col-span-3">
          <Button aria-label={unsubTelemarketing} variant="secondary" fullWidth onClick={handleUnsubPhoneCallClick}>
            Unsubscribe From Phone Calls
          </Button>
        </div>
      </div>
      {PhoneModal()}
      {NoPhoneModal()}
    </div>
  );
}

function TelemarketingPreferences({ phoneNumber }) {
  return (
    <ErrorBoundary>
      <BannerProvider>
        <Preferences phoneNumber={phoneNumber} />
      </BannerProvider>
    </ErrorBoundary>
  );
}

export default TelemarketingPreferences;

const propTypes = {
  phoneNumber: PropTypes.string,
};

const defaultProps = {
  phoneNumber: '',
};

TelemarketingPreferences.propTypes = propTypes;
TelemarketingPreferences.defaultProps = defaultProps;
Preferences.propTypes = propTypes;
Preferences.defaultProps = defaultProps;
